import { gql } from "@apollo/client";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FieldArray, Form, Formik } from "formik";
import { Fragment, useRef } from "react";
import * as yup from "yup";

import {
  useCollectionNoteLinesUpdateInvoicedLineEwcCodeMutation,
  useCollectionNoteLinesUpdateLinesMutation,
} from "../generated/graphql";
import { getGraphQLErrorMessage } from "../utils/getGraphQLErrorMessage";
import { CollectionNotesTableField } from "./CollectionNotesTableField";
import { Panel } from "./Panel";

export const UPDATE_COLLECTION_NOTE_LINES = gql`
  mutation CollectionNoteLinesUpdateLines($input: UpdateCollectionNoteLinesInput!) {
    updateCollectionNoteLines(input: $input) {
      id
      collectionNoteLines {
        id
        quantity
        description
        invoiced
        value
        vat
        total

        ewcCode {
          id
          code
          product
          isArchived
        }

        nominalCode {
          id
          code
        }
      }
    }
  }
`;

export const UPDATE_INVOICED_COLLECTION_NOTE_LINE_EWC_CODE = gql`
  mutation CollectionNoteLinesUpdateInvoicedLineEwcCode(
    $input: UpdateInvoicedCollectionNoteLineEWCCodeInput!
  ) {
    updateInvoicedCollectionNoteLineEWCCode(input: $input) {
      id
      canChangeEwcCode
      ewcCodeChangedSinceInvoiced
      ewcCodeChangedAt

      ewcCode {
        id
        code
        product
        isArchived
      }
    }
  }
`;

const CollectionNoteLinesSchema = yup.object().shape({
  collectionNoteLines: yup.array(
    yup.object().shape({
      id: yup.string().nullable(),
      quantity: yup.string().nullable(),
      description: yup.string().nullable(),
      ewcCodeId: yup.string().nullable(),
      nominalCodeId: yup.string().nullable(),
      value: yup.number().nullable(),
      vat: yup.number().nullable(),
      total: yup.number().nullable(),
    })
  ),
});

interface EWCCode {
  id: string;
  code: string;
  product: string;
  note?: string | null;
  isArchived?: boolean;
}

interface NominalCode {
  id: string;
  code: string;
}

interface CollectionNoteLine {
  id: string | null;
  quantity: string;
  description: string;
  ewcCodeId: string;
  nominalCodeId: string;
  value: string;
  vat: string;
  total: string;
  invoiced: boolean;
  invoiceId?: string;
  invoiceReference?: string;
  archivedEwcCode?: EWCCode | null;
  canChangeEwcCode?: boolean;
  invoicedEwcCode?: EWCCode | null;
  ewcCodeChangedAt?: string | null;
}

interface CollectionNoteLinesProps {
  isEditable: boolean;
  collectionNoteId: string;
  collectionNoteLines: CollectionNoteLine[];
  ewcCodes: EWCCode[];
  nominalCodes: NominalCode[];
}

/**
 * Invoiced lines whose EWC code has been corrected in the form. These are saved one by one with
 * the dedicated mutation, as the generic line update rejects any change to an invoiced line.
 */
const getInvoicedEwcCodeChanges = (
  initialLines: CollectionNoteLine[],
  lines: CollectionNoteLine[]
) => {
  return lines.filter((line) => {
    if (!line.id || !line.invoiced || !line.canChangeEwcCode) {
      return false;
    }

    const initialLine = initialLines.find((initial) => initial.id === line.id);

    return Boolean(line.ewcCodeId) && initialLine?.ewcCodeId !== line.ewcCodeId;
  });
};

export const CollectionNoteLines: React.FC<CollectionNoteLinesProps> = ({
  isEditable,
  collectionNoteId,
  collectionNoteLines,
  ewcCodes,
  nominalCodes,
}) => {
  const toast = useToast();
  const confirmation = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [updateCollectionNoteLines] = useCollectionNoteLinesUpdateLinesMutation();
  const [updateInvoicedLineEwcCode] = useCollectionNoteLinesUpdateInvoicedLineEwcCodeMutation({
    refetchQueries: ["GetCollectionNote"],
    awaitRefetchQueries: true,
  });

  const canChangeInvoicedEwcCodes = collectionNoteLines.some((line) => line.canChangeEwcCode);

  return (
    <Panel overflowY="auto">
      <Formik
        enableReinitialize
        initialValues={{
          collectionNoteLines:
            collectionNoteLines.length > 0 ? collectionNoteLines : [makeNewCollectionNoteLine()],
        }}
        validationSchema={CollectionNoteLinesSchema}
        onSubmit={async (values) => {
          const ewcCodeChanges = getInvoicedEwcCodeChanges(
            collectionNoteLines,
            values.collectionNoteLines
          );

          if (!isEditable && ewcCodeChanges.length === 0) {
            return;
          }

          try {
            for (const line of ewcCodeChanges) {
              await updateInvoicedLineEwcCode({
                variables: {
                  input: { collectionNoteLineId: line.id as string, ewcCodeId: line.ewcCodeId },
                },
              });
            }

            if (isEditable) {
              const formattedLines = values.collectionNoteLines.map((line) => {
                return {
                  id: line.id,
                  quantity: line.quantity,
                  description: line.description,
                  ewcCodeId: line.ewcCodeId,
                  nominalCodeId: line.nominalCodeId,
                  value: line.value,
                  vat: line.vat,
                  total: line.total,
                };
              });

              await updateCollectionNoteLines({
                variables: {
                  input: {
                    collectionNoteId,
                    collectionNoteLines: formattedLines,
                  },
                },
              });
            }

            toast({ title: "Note lines saved", status: "success" });
          } catch (e) {
            toast({ title: getGraphQLErrorMessage(e), status: "error" });
          }
        }}
      >
        {({ values, isSubmitting, submitForm }) => {
          // Lines that will no longer match the code they were invoiced with. Switching a line
          // back to its invoiced code doesn't need confirming.
          const mismatchedLines = getInvoicedEwcCodeChanges(
            collectionNoteLines,
            values.collectionNoteLines
          ).filter((line) => line.ewcCodeId !== line.invoicedEwcCode?.id);

          const invoiceReferences = Array.from(
            new Set(mismatchedLines.map((line) => line.invoiceReference).filter(Boolean))
          ).join(", ");

          return (
            <Form>
              <FieldArray name="collectionNoteLines">
                {(arrayHelpers) => {
                  return (
                    <Fragment>
                      <CollectionNotesTableField
                        name="collectionNoteLines"
                        data={values.collectionNoteLines}
                        ewcCodes={ewcCodes}
                        nominalCodes={nominalCodes}
                        onRemoveRow={arrayHelpers.remove}
                      />

                      {isEditable || canChangeInvoicedEwcCodes ? (
                        <Flex justifyContent="space-between" alignItems="center" px={4} py={4}>
                          {isEditable ? (
                            <Button
                              tye="button"
                              colorScheme="teal"
                              onClick={() => {
                                arrayHelpers.push(makeNewCollectionNoteLine());
                              }}
                            >
                              Add a new row
                            </Button>
                          ) : (
                            <span />
                          )}

                          <Button
                            type="button"
                            colorScheme="blue"
                            isDisabled={isSubmitting}
                            onClick={() => {
                              if (mismatchedLines.length > 0) {
                                confirmation.onOpen();
                                return;
                              }

                              submitForm();
                            }}
                          >
                            Save changes
                          </Button>
                        </Flex>
                      ) : null}
                    </Fragment>
                  );
                }}
              </FieldArray>

              <AlertDialog
                isOpen={confirmation.isOpen}
                leastDestructiveRef={cancelRef}
                onClose={confirmation.onClose}
              >
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      Change invoiced EWC code?
                    </AlertDialogHeader>

                    <AlertDialogBody>
                      Changing the EWC code on {mismatchedLines.length} invoiced{" "}
                      {mismatchedLines.length === 1 ? "line" : "lines"} means this collection note
                      will no longer match{" "}
                      {invoiceReferences ? `invoice ${invoiceReferences}` : "its invoice"}. The
                      invoice may show a different code.
                    </AlertDialogBody>

                    <AlertDialogFooter>
                      <Button ref={cancelRef} onClick={confirmation.onClose}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="orange"
                        ml={3}
                        onClick={() => {
                          confirmation.onClose();
                          submitForm();
                        }}
                      >
                        Save anyway
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>
            </Form>
          );
        }}
      </Formik>
    </Panel>
  );
};

const makeNewCollectionNoteLine = () => {
  return {
    id: null,
    quantity: "",
    description: "",
    ewcCodeId: "",
    nominalCodeId: "",
    value: "",
    vat: "",
    total: "",
    invoiced: false,
  };
};
